// OverviewHeader.jsx - Separate Header Component
import React from 'react';
import { Button, Box, Typography, Select, MenuItem, CircularProgress } from '@mui/material';
import { ReactComponent as Dropdown } from "../../../assets/images/dropdown-arrow.svg";
import DateRangeSelector from './DateRangeSelector';
import GroupSelectDropdown from '../ProfileComponents/GroupSelectDropdown';
import { isHeaderInlinePageTitleEnabled } from '../../../config/featureFlags';


function MainHeaderAnalytics({
  title = "Overview",
  dateRange,
  onDateChange,
  selectedGroupId,
  onGroupChange,
  onExport,
  exportSectionOptions = [],
  exportSection,
  onExportSectionChange,
  exportLoading = false,

  headerStyle = {}
}) {
  const hasExportSections = Array.isArray(exportSectionOptions) && exportSectionOptions.length > 0;
  const safeExportSection =
    exportSection && exportSectionOptions.some((o) => o.value === exportSection)
      ? exportSection
      : (exportSectionOptions[0]?.value ?? '');

  // Default text style
  const textStyle = {
    color: '#405261',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isHeaderInlinePageTitleEnabled ? 'flex-end' : 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: 1.5,
        mb: '24px',
        bgcolor: '#fff',
        zIndex: 10,
        gap: 1,
        ...headerStyle,
      }}
    >
      {/* Legacy in-page title — hidden when header shows inline title (Application V2) */}
      {!isHeaderInlinePageTitleEnabled && (
        <Typography className="overview_main" sx={{ minWidth: 0 }}>
          {title}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
        <DateRangeSelector
          dateRange={dateRange}
          onDateChange={onDateChange}
          textStyle={textStyle}
        />

        {onGroupChange && !isHeaderInlinePageTitleEnabled ? (
          <GroupSelectDropdown
            value={selectedGroupId}
            onSelect={onGroupChange}
          />
        ) : null}

        {hasExportSections ? (
          <Select
            value={safeExportSection}
            onChange={(e) => onExportSectionChange?.(e.target.value)}
            disabled={exportLoading}
            IconComponent={Dropdown}
            displayEmpty
            renderValue={(selected) => {
              const opt = exportSectionOptions.find((o) => o.value === selected);
              return (
                <span
                  style={{
                    color: "#405261",
                    fontSize: "14px",
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 220,
                  }}
                  title={opt?.label}
                >
                  {opt?.label ?? selected}
                </span>
              );
            }}
            sx={{
              height: "40px",
              minWidth: "200px",
              maxWidth: "280px",
              fontSize: "14px",
              color: "#405261",
              fontFamily: "Inter",
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
              '&:hover': {
                border: '1px solid #9CA3AF',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
          
              '& .MuiSelect-select': {
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              },
              "& .MuiSelect-icon": {
                fontSize: "16px",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiSelect-iconOpen": {
                fontSize: "16px",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%) rotate(180deg)",
                right: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #D5DCE1",
                  marginTop: "4px",
                  minWidth: "220px",
                  maxWidth: "320px",
                  borderRadius: "8px",
                  background: '#FFF',
                  boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
                  '& .MuiMenuItem-root': {
                    fontSize: "14px",
                    fontFamily: 'Inter',
                    padding: "8px 12px",
                    color: "#405261",
                    borderRadius: "8px",
                    margin: "2px 4px",
                    whiteSpace: 'normal',
                    '&:hover, &.Mui-selected': {
                      color: '#061522',
                      backgroundColor: "#F9FAFA !important",
                    },
                  },
                },
              },
            }}
          >
            {exportSectionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} title={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        ) : null}

        <Button
          type="button"
          variant="outlined"
          onClick={onExport}
          disabled={!onExport || exportLoading}
          className='export-button'
          sx={{
            minWidth: 88,
            height: 40,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {exportLoading ? (
            <CircularProgress size={20} thickness={4} sx={{ color: '#405261' }} />
          ) : (
            <span style={textStyle}>Export</span>
          )}
        </Button>
      </Box>
    </Box>
  );
}

export default MainHeaderAnalytics;
